import { applyNodeChanges, type Node, type NodeChange } from '@xyflow/react';
import type { Rect } from './edgeGeometry';

type SizedNodeData = {
  width?: number;
  height?: number;
};

export function applyControlledNodeChanges<NodeType extends Node>(
  changes: NodeChange<NodeType>[],
  nodes: NodeType[]
): NodeType[] {
  return applyNodeChanges(changes, nodes);
}

/**
 * FSM states are not resizable. Drop `dimensions` reports so React Flow
 * measurement of the 15×15 initial circle cannot copy a new nodes array
 * (and then a new edges array) on every layout pass — the React #185 loop
 * when adding a create-transition.
 */
export function applyFsmControlledNodeChanges<NodeType extends Node>(
  changes: NodeChange<NodeType>[],
  nodes: NodeType[]
): NodeType[] {
  const next = changes.filter((change) => change.type !== 'dimensions');
  if (next.length === 0) return nodes;
  return applyControlledNodeChanges(next, nodes);
}

/** Reuse `previous` when every item is the same object, so React Flow does not see a new array. */
export function reuseUnchangedList<T>(previous: T[], next: T[]): T[] {
  if (previous.length !== next.length) return next;
  for (let i = 0; i < next.length; i++) {
    if (previous[i] !== next[i]) return next;
  }
  return previous;
}

export function flowNodeRects<NodeType extends Node<SizedNodeData>>(
  nodes: NodeType[]
): Map<string, Rect> {
  const rects = new Map<string, Rect>();

  for (const node of nodes) {
    const width = node.width ?? node.measured?.width ?? node.data.width;
    const height = node.height ?? node.measured?.height ?? node.data.height;
    if (width == null || height == null) continue;

    rects.set(node.id, {
      x: node.position.x,
      y: node.position.y,
      width,
      height,
    });
  }

  return rects;
}
